/**
 * A Box 1 value carrying a state registration number after the EPA ID.
 *
 * Texas manifests routinely print the TCEQ Solid Waste Registration number
 * immediately after the generator's EPA ID — "TXD982556581/38544" — because Box 1
 * is the only place on the federal form to put it. Read verbatim it is not a
 * valid EPA ID, so the compliance engine rejects it, while the form's own *TX SWR
 * Number* field sits empty beside it holding exactly the half that was rejected.
 *
 * The extraction prompt splits these going forward. This is for the values
 * already stored, and for the readings where the model did not.
 */

/**
 * An EPA ID: a two-letter state or territory code followed by ten alphanumeric
 * characters.
 *
 * Anchored to the whole leading part, because a partial match is what turns a
 * hyphenated ID into a false split. `TXR000012345` must not be read as `TXR00001`
 * plus a registration number.
 */
const EPA_ID = /^[A-Z]{2}[A-Z0-9]{10}$/;

/**
 * The separator between the two. A slash is most common; a space, hyphen or
 * comma appears often enough on handwritten forms to be worth accepting.
 *
 * Requires at least one, and allows surrounding whitespace, so "TXD982556581 /
 * 38544" splits the same way as "TXD982556581/38544".
 */
const SEPARATOR = /\s*[/\-,]\s*|\s+/;

export interface EpaIdSplit {
  epaId: string;
  registrationNumber: string;
}

/**
 * The two halves of a combined Box 1 value, or null when there is nothing to
 * split.
 *
 * Null rather than a guess in every uncertain case. Offering a wrong split is
 * worse than offering none: the reviewer is here because they cannot read the
 * document, so they are in the weakest position to notice that the EPA ID they
 * just accepted is missing its last two characters.
 *
 * Deliberately strict about what counts as an EPA ID. A value that is merely
 * long, or merely contains a separator, is left alone — "SEE ATTACHED" and
 * "TXD982556581-PENDING" are not splits, and the second is the dangerous one,
 * since it would otherwise file "PENDING" as a registration number.
 */
export function splitEpaIdAndRegistration(value: string | null | undefined): EpaIdSplit | null {
  if (value == null) return null;

  const trimmed = value.trim().toUpperCase();
  if (trimmed.length === 0) return null;

  // A clean EPA ID on its own has nothing to split.
  if (EPA_ID.test(trimmed)) return null;

  const parts = trimmed.split(SEPARATOR).filter((part) => part.length > 0);
  if (parts.length !== 2) return null;

  const [epaId, registrationNumber] = parts;
  if (!EPA_ID.test(epaId)) return null;
  // Not itself an EPA ID, which would mean this is two IDs rather than an ID and
  // a registration.
  if (EPA_ID.test(registrationNumber)) return null;
  // And it has to contain a digit. A registration number has no fixed format —
  // Texas issues plain numbers — but every issuer's is numbered, so a purely
  // alphabetic tail is a word somebody typed after the ID rather than a
  // registration: "TXD982556581-PENDING" would otherwise file "PENDING" as one,
  // which is the case this function exists to refuse.
  if (!/[0-9]/.test(registrationNumber)) return null;

  return { epaId, registrationNumber };
}

/**
 * Whether this generator is one the Texas registration number belongs to.
 *
 * The split itself is state-agnostic — an EPA ID followed by a numbered tail
 * looks the same in any state — but the only field on this form to put the tail
 * in is Texas's. Offering the split elsewhere would file a California
 * generator's number in a column labelled Texas, on a screen whose reader is
 * transcribing a document they already cannot read well.
 *
 * The EPA ID carries the state in its first two characters, which is what makes
 * this answerable before either address has been filled in.
 */
export function isTexasGenerator({
  epaId,
  mailState,
  locationState,
}: {
  epaId: string | null | undefined;
  mailState: string | null | undefined;
  locationState: string | null | undefined;
}): boolean {
  return (
    mailState === "TX" ||
    locationState === "TX" ||
    epaId?.trim().toUpperCase().startsWith("TX") === true
  );
}
